import { defineComponent } from 'vue';
import { QCard, QCardSection, QIcon, QBadge, QTree } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import { normalizeIconName } from '../../../Shared/buttonAppearance.js';
import BaseComponentMixin from './BaseComponentMixin.jsx';

function resolveMenuIcon(raw) {
    if (raw == null || raw === '')
        return null;

    const iconVal = typeof raw === 'object' && raw.Icon != null ? raw.Icon : raw;
    return normalizeIconName(iconVal) || null;
}

function ensureTreeIds(nodes, prefix = 'n') {
    if (!Array.isArray(nodes))
        return [];
    return nodes.map((node, i) => {
        const id = node.id != null ? String(node.id) : `${prefix}_${i}`;
        const copy = { ...node, id };
        if (node.children && node.children.length > 0)
            copy.children = ensureTreeIds(node.children, id);
        return copy;
    });
}

function collectParentIds(nodes, out = []) {
    for (const n of nodes || []) {
        if (n.children && n.children.length > 0) {
            out.push(n.id);
            collectParentIds(n.children, out);
        }
    }
    return out;
}

const defaultSampleModel = [
    {
        name: 'Sample 1',
        icon: 'mdi mdi-cog',
        tooltip: 'A sample',
        item_data: {
            MenuItemData: {
                Badge: {
                    CountURL: '123',
                    ToolTip: 'Badge Tooltip',
                },
            },
        },
    },
    {
        name: 'Sample 2',
        icon: 'mdi mdi-cogs',
        tooltip: 'A sample 2',
        item_data: {
            MenuItemData: {
                Badge: null,
            },
        },
    },
];

export default defineComponent({
    name: 'dynamic-menu-list-dsgn',
    mixins: [BaseComponentMixin],
    components: {
        QCard,
        QCardSection,
        QIcon,
        QBadge,
        QTree,
    },
    data() {
        return {
            model: [],
            expandedKeys: [],
        };
    },
    created() {
        let raw;
        if (this.controlData?.SourceType === 'Raw' && this.controlData.SourceRaw) {
            if (Array.isArray(this.controlData.SourceRaw))
                raw = this.controlData.SourceRaw;
            else
                raw = defaultSampleModel;
        }
        else
            raw = defaultSampleModel;

        this.model = ensureTreeIds(raw);
        this.expandedKeys = collectParentIds(this.model);
    },
    render() {
        if (!this.controlData)
            return null;

        const style = {
            minWidth: '25px',
            ...utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent),
            ...utils.getStyleHints(this.controlData.StyleHints),
        };

        let title;
        if (this.controlData.MenuTitle)
            title = (
                <QCardSection
                    class="text-white"
                    style={{ backgroundColor: '#3949ab', padding: '12px 16px' }}>
                    {this.controlData.MenuTitle}
                </QCardSection>
            );

        const renderNodeHeader = ({ node }) => {
            let inner;
            const iconName = resolveMenuIcon(node.icon);
            if (iconName)
                inner = (
                    <span class="text-wrap row items-center no-wrap">
                        <QIcon name={iconName} size="sm" class="q-mr-xs" />
                        {node.name}
                    </span>
                );
            else
                inner = <span class="text-wrap">{node.name}</span>;

            if (node.item_data?.MenuItemData?.Badge) {
                const badgeinfo = node.item_data.MenuItemData.Badge;
                const label = badgeinfo.CountURL != null ? String(badgeinfo.CountURL) : '';
                inner = (
                    <span class="relative-position" style={{ display: 'inline-block' }}>
                        {inner}
                        <QBadge floating color="negative" label={label || ' '} />
                    </span>
                );
            }

            if (node.tooltip)
                inner = utils.tt(node.tooltip, inner);

            return (
                <div class="q-pa-xs row items-center no-wrap" style={{ minWidth: 0 }}>
                    {inner}
                </div>
            );
        };

        return (
            <div
                class={{
                    'c-DynamicMenuList': true,
                    [`c-name-${this.name || 'unnamed'}`]: true,
                    'designer-container': true,
                    selected: this.selected$,
                }}
                style={style}
                onClick={(e) => this.handleClick(e)}>
                <div class={{ 'designer-container-hovering': true, 'designer-container-selected-clickthrough': this.selected$ }} />

                <QCard class="q-mx-auto" flat bordered style={{ borderRadius: '8px' }}>
                    {title}

                    <QCardSection class="q-pa-none ma-1">
                        <QTree
                            nodes={this.model}
                            node-key="id"
                            label-key="name"
                            noConnectors
                            class="c-DynamicMenuList-tree"
                            expanded={this.expandedKeys}
                            onUpdate:expanded={(keys) => { this.expandedKeys = keys; }}
                            v-slots={{ 'default-header': renderNodeHeader }}
                        />
                    </QCardSection>
                </QCard>
            </div>
        );
    },
});
